import Head from 'next/head';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import React, { useMemo } from 'react';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import Layout from '@/layouts';
import { fetchLoadingDataWith } from '@/lib/service';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3WithWebApplicationJsonLd } from '@/lib/jsonld';
import { getRevalidateTime, SHOW_ISR_DEBUG_PANEL, SITE_HOST, ISR_ENV } from '@/shared/isr-config';
import ISRDebugPanel from '@/components/Common/ISRDebugPanel';

const IgFollowerExportContent = dynamic(() => import('@/components/IgFollowerExport'), {
  ssr: true,
});

export const getStaticProps: GetStaticProps = async (context) => {
  const { locale } = context;
  const path = '/ig-follower-export-tool';

  // 1. 并行请求数据，添加 .catch() 防止构建失败
  const [localeRes, tdkRes, fetchDataResRaw] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(path, locale),
    fetchLoadingDataWith(path, locale).catch((): null => null),
  ]);

  // 2. 语言 Fallback：当前语言无数据时请求英文
  let fetchDataRes = fetchDataResRaw;
  if (!fetchDataRes?.data && locale !== 'en') {
    fetchDataRes = await fetchLoadingDataWith(path, 'en').catch((): null => null);
  }

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const siteUrl = locale !== 'en' ? `https://${SITE_HOST}/${locale}` : `https://${SITE_HOST}`;
  const jsonLd = page3WithWebApplicationJsonLd(
    {
      title: 'Ig Follower Export Tool',
      desc: tdkRes?.data?.description ?? tempData?.head_Hashtag_Meta_Desc ?? null,
      url: canonicalUrl(SITE_HOST, path, locale),
    },
    {
      title: 'Toolkit',
      url: siteUrl + '/toolkit',
    },
    { url: siteUrl },
    {
      name: 'Instagram Follower Export Tool – DolphinRadar',
      url: 'https://www.dolphinradar.com/ig-follower-export-tool',
      applicationCategory: 'Instagram Follower Export Tool',
      description:
        'Easily export Instagram followers or following from any public account. Download to CSV or Excel for social data management — fast, secure, and anonymous.',
      featureList: [
        'Export Instagram followers',
        'Download Instagram following list',
        'Instagram follower export tool',
      ],
    }
  );

  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(SITE_HOST, path, locale),
      fetchDataRes: fetchDataRes?.data ?? [],
      locale,
      title: tdkRes?.data?.title ?? tempData?.head_Hashtag_Meta_Title ?? null,
      description: tdkRes?.data?.description ?? tempData?.head_Hashtag_Meta_Desc ?? null,
      jsonLd,
      // ISR 调试信息
      __isr_generated_at: Date.now(),
      __isr_revalidate: getRevalidateTime('ig-follower-export-tool'),
      __isr_page_name: 'ig-follower-export-tool',
      __isr_env: ISR_ENV,
    },
    revalidate: getRevalidateTime('ig-follower-export-tool'),
  };
};

const PageContent: NextPageWithLayout<InferGetStaticPropsType<typeof getStaticProps>> = (props) => {
  return useMemo(
    () => (
      <>
        <IgFollowerExportContent data={props} />
        {SHOW_ISR_DEBUG_PANEL && (
          <ISRDebugPanel
            pageName={props.__isr_page_name}
            generatedAt={props.__isr_generated_at}
            revalidate={props.__isr_revalidate}
            env={props.__isr_env}
          />
        )}
      </>
    ),
    [props]
  );
};

PageContent.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default PageContent;
